import React, { forwardRef } from 'react';
import cn from 'classnames';
import { Box } from '@fiverr-private/theme';
import { GoSpinnerIcon } from '../../icons';
import { BaseButtonProps } from './BaseButton.types';
import useGetButtonElement from './useGetButtonElement';
import { getButtonColor, getClassNames, getStylingProps } from './BaseButton.style';

export const BaseButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, BaseButtonProps>(
    (
        {
            children,
            href,
            loading = false,
            disabled = false,
            active = false,
            className,
            variant = 'primary',
            borderRadius = 'circle',
            backgroundColor,
            borderColor = 'transparent',
            color,
            ...rest
        },
        ref
    ) => {
        const { element, role } = useGetButtonElement(href);
        const stylingProps = getStylingProps(variant, loading, disabled, borderColor);
        const currentColor = color || stylingProps.color;

        return (
            <Box
                ref={ref}
                {...stylingProps}
                {...rest}
                backgroundColor={backgroundColor || stylingProps.backgroundColor}
                borderRadius={borderRadius}
                color={loading ? 'transparent' : currentColor}
                as={element}
                role={role}
                href={href}
                disabled={disabled}
                tabIndex={href && disabled ? -1 : undefined}
                className={cn(className, getClassNames(variant, active, disabled, loading))}
            >
                {children}
                {loading && (
                    <Box
                        as="span"
                        visibility={loading ? 'visible' : 'hidden'}
                        position="absolute"
                        display="inlineFlex"
                        style={{ color: getButtonColor(variant, disabled) }}
                    >
                        <GoSpinnerIcon size={22} />
                    </Box>
                )}
            </Box>
        );
    }
);

BaseButton.displayName = 'BaseButton';
